/*
 *
 * MultiMerchantSelect
 *
 * @version 1.0.0
 * @author Stefano Servodidio
 * @since 20-Nov-2024
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { Button, Popover } from '@mui/material'
import { LineHorizontal } from '@nexidigital/iconlibrary'
import PortalIconButton from '@nexidigital/portaliconbutton'
import './MultiMerchantSelect.scss'
import PortalLoader from '@nexidigital/portalloader'
import { debounce } from 'lodash'
import MerchantSearch from 'Components/MerchantSearch'
import formattedMessage, { messages } from './languageModule'

const MultiMerchantSelect = (props) => {
  const {
    merchantGroup,
    hasMultiRole,
    hasSubRole,
    actions,
    loading,
    search,
    removeMobileMenuOverflow,
    hasSwitchableMerchants: storedHasSwitchableMerchants
  } = props
  const intl = useIntl()
  const buttonRef = useRef(null)

  const count =
    merchantGroup?.reduce?.((acc, group) => {
      if (!group?.merchants?.length) return acc
      return acc + group.merchantCounter
    }, 0) || 0

  const [isOpen, setOpen] = useState(false)
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [hasSwitchableMerchants] = useState(
    () => storedHasSwitchableMerchants ?? (hasSubRole && count > 1)
  )
  const [popperWidth, setPopperWidth] = useState(buttonRef?.current?.offsetWidth || 360)

  useEffect(() => {
    if (storedHasSwitchableMerchants === null || storedHasSwitchableMerchants === undefined) {
      actions.doSetSwitchableMerchants?.(hasSubRole && count > 1)
    }
  }, [actions, count, hasSubRole, storedHasSwitchableMerchants])

  const searchMerchants = useCallback(
    debounce((search) => actions.doGetUserProfile({ search }), 500),
    []
  )

  useEffect(() => {
    if (search?.length >= 3) {
      searchMerchants(search)
    }
  }, [search])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleResize = () => {
    setPopperWidth(buttonRef?.current?.offsetWidth || 360)
  }

  const handleClick = (event) => {
    setOpen(event.currentTarget)
    removeMobileMenuOverflow()
  }

  const handleKeyUp = (event) => {
    if (event.key === 'Enter') {
      setOpen(event.currentTarget)
    }
  }

  const handleClose = () => {
    setOpen(null)
  }

  const handleMerchantClick = (merchant) => {
    actions.doGetUserProfile({ selected: merchant.merchantId, tenantId: merchant.tenantId })

    // at click you close the select and clean the array & select
    handleClose()
    actions.doSetMerchantSearch('')
  }
  
  if (!hasMultiRole) {
    return null
  }

  const selectedMerchantInfo = (
    <div className='merchantSelectButtonLabel'>
      <div className='merchantSelectButtonLabel_title'>{formattedMessage.pointOfSale}</div>
      <div className='merchantSelectButtonLabel_subtitle'>
        {props.businessName} - ID: <span>{props.merchantId}</span>
      </div>
    </div>
  )

  if (!hasSwitchableMerchants) {
    return (
      <div className='merchantSelectReadonly'>{selectedMerchantInfo}</div>
    )
  }

  return (
    <div className='merchantSelect'>
      <Button
        ref={buttonRef}
        className={`merchantSelectButton ${isOpen ? 'open' : ''}`}
        aria-label='Merchant select'
        onClick={handleClick}
        onKeyUp={handleKeyUp}
        disableRipple>
        {selectedMerchantInfo}
        <PortalIconButton
          ariaLabel={`${isOpen ? 'Open' : 'Close'} point of sale`}
          icon={<LineHorizontal size='24' type='fill' />}
        />
      </Button>
      <Popover
        id='merchantSelectPopover'
        open={!!isOpen}
        anchorEl={buttonRef?.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        slotProps={{
          paper: {
            className: 'merchantSelectPopover',
            sx: {
              width: popperWidth,
              maxHeight: 'calc(100vh - 80px)'
            }
          }
        }}>
        <div className='merchantSelectPopoverHeader'>
          <MerchantSearch
            aria-label={intl.formatMessage(messages.search_alt)}
            cancelLabel={intl.formatMessage(messages.cancel_label)}
            placeholder={intl.formatMessage(messages.search_placeholder)}
            value={search}
            onChange={(e) => actions.doSetMerchantSearch(e.target.value)}
            onFocus={(e) => {
              setIsSearchFocused(true)
              e.target.placeholder = ''
            }}
            onBlur={(e) => {
              setIsSearchFocused(false)
              e.target.placeholder = intl.formatMessage(messages.search_placeholder)
            }}
            onClear={() => {
              actions.doSetMerchantSearch('')
              actions.doGetUserProfile({ search: '' })
            }}
            focused={isSearchFocused}
            disabled={loading}
            className='merchantSelectPopoverSearch'
          />
          {loading && <PortalLoader />}
          {!loading && (
            <div className='merchantGroupCount'>
              {formattedMessage.results}: <span>{count}</span>
            </div>
          )}
        </div>
        {!loading && !!merchantGroup?.length && (
          <div className='merchantSelectPopoverBody'>
            <div className='merchantGroupList'>
              {props.merchantGroup.map((group, index) => (
                <div className='merchantGroupListItem' key={`group-${group.name}-${index}`}>
                  <div className='merchantGroupItemDetail'>
                    {group.name || ' '} - {group.groupBy.toUpperCase() || ''}: {group.value || ''}
                  </div>
                  <div className='merchantList'>
                    {group?.merchants?.map?.((merchant, index) => (
                      <div
                        role='button'
                        tabIndex={0}
                        className={`merchantListItem ${props.merchantId === merchant.merchantId ? 'selected' : ''}`}
                        key={`merchant-${merchant.merchantId}${index}`}
                        onClick={() => handleMerchantClick(merchant)}
                        onKeyUp={(e) => {
                          if (e.key === 'Enter') {
                            handleMerchantClick(merchant)
                          }
                        }}>
                        <div className='merchantName'>{merchant.businessName || ''}</div>
                        <div className='merchantId'>
                          {formattedMessage.merchantId}
                          <span>{merchant.merchantId || ''}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {!loading && count > 10 && (
              <div className='merchantGroupEmptyMessage'>{formattedMessage.moreResults}</div>
            )}
          </div>
        )}
      </Popover>
    </div>
  )
}

MultiMerchantSelect.propTypes = {
  merchantId: PropTypes.string,
  businessName: PropTypes.string,
  merchantGroup: PropTypes.array,
  actions: PropTypes.object,
  loading: PropTypes.bool,
  hasMultiRole: PropTypes.bool,
  hasSubRole: PropTypes.bool,
  search: PropTypes.string,
  hasSwitchableMerchants: PropTypes.bool,
  removeMobileMenuOverflow: PropTypes.func
}

MultiMerchantSelect.defaultProps = {
  merchantId: '',
  businessName: '',
  merchantGroup: [],
  actions: {},
  loading: false,
  hasMultiRole: false,
  hasSubRole: false,
  search: '',
  hasSwitchableMerchants: null,
  removeMobileMenuOverflow: null
}

export default MultiMerchantSelect
